import React from 'react';
import { fetcher } from './axios';
import Compressor from 'compressorjs';
import { getImageUrl } from './getImageUrl';
import { TemplateVarsData } from 'types/templates/templates-menu';
import {
  FetchSendTemplateMessageParams,
  getTemplateFormattedObject,
  GetTemplateFormattedObjectParams,
} from 'api/templates';
import { WhatsappOriginalTemplate, WhatsappTemplate } from 'types/templates';
import { store } from 'store';

export function getNestedValue<T>(obj: T, path: string): any {
  return path.split('.').reduce((acc: any, key) => acc && acc[key], obj);
}
export function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

//* Languages
export function isEnglishString(str: string) {
  const englishRegex = /^[a-zA-Z0-9\s.,!?]+$/;
  return englishRegex.test(str);
}
export function isHebrewString(str: string) {
  const hebrewRegex = /[\u0590-\u05FF]+/;
  return hebrewRegex.test(str);
}

//* JSON
export function IsJsonString(str: string | null | undefined): boolean {
  if (typeof str !== 'string') return false;
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}
function escapeJsonString(value: string) {
  if (typeof value === 'string') {
    return value
      .replace(/\\/g, '\\\\') // Escaping backslash
      .replace(/"/g, '\\"') // Escaping quotes
      .replace(/\n/g, '\\n') // Escaping breaklines
      .replace(/\r/g, '\\r') //
      .replace(/\t/g, '\\t'); // Escaping tabs
  }
  return value; // If not string - return value itself
}
export const sanitizeJson: (obj: any) => typeof obj = (obj) => {
  if (typeof obj === 'string') {
    return escapeJsonString(obj);
  } else if (Array.isArray(obj)) {
    return obj.map(sanitizeJson);
  } else if (typeof obj === 'object' && obj !== null) {
    const sanitizedObject: {
      [key: string]: any;
    } = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sanitizedObject[key] = sanitizeJson(obj[key]);
      }
    }
    return sanitizedObject;
  }
  return obj;
};
export function safeParseJSON(jsonString: string | null | undefined) {
  try {
    if (!jsonString) return null;
    if (!IsJsonString(jsonString)) return jsonString;
    return JSON.parse(jsonString);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // console.error('Invalid JSON format:', jsonString, error);
    return null;
  }
}

//* Generate ID
export function generateUniqueId(existingIds: string[] = [], length: number = 5) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;

  let uniqueId = '';
  let isUnique = false;

  while (!isUnique) {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    if (!existingIds.includes(result)) {
      uniqueId = result;
      isUnique = true;
    }
  }

  return uniqueId;
}
export const generateIncrementalId = (items: any[]) => {
  if (items.length === 0) return 1; // if empty array - return 1 as first ID
  const maxId = Math.max(...items.map((item) => item.id));
  return maxId + 1; // New id is the max id + 1
};

//* Colors
export function getContrastYIQ(hexcolor: string) {
  if (!hexcolor) return hexcolor;

  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? 'black' : 'white';
}

//* Links
export async function getFileLinkFromServer(e: React.ChangeEvent<HTMLInputElement>) {
  try {
    const file = e.target.files?.[0];
    if (!file) {
      console.error('No file selected');
      return null;
    }

    const formData = new FormData();
    if (file.type.startsWith('image/')) {
      formData.append('image', file);
      const data = await fetcher('post', ['/api/services/GetImageLink', { data: formData }], 'history');
      if (data?.result === true) {
        return {
          Name: file.name,
          Link: data.link,
        };
      } else return null;
    } else {
      formData.append('file', file);
      const data = await fetcher('post', ['/api/whatsapp/SaveFile', { data: formData }], 'history');
      if (data?.result === true) {
        return {
          Name: file.name,
          Link: data.link,
        };
      } else return null;
    }
  } catch (error) {
    console.error(error);
    return null;
  } finally {
    e.target.value = '';
  }
}
export const isValidUrl = (string: string) => {
  let url;

  try {
    url = new URL(string);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
};
export function getPathname(): string {
  return window.location.pathname;
}

//* HTML
export function getElementOffset(el: Element) {
  const box = el.getBoundingClientRect();

  return {
    top: box.top + window.scrollY,
    right: box.right + window.scrollX,
    bottom: box.bottom + window.scrollY,
    left: box.left + window.scrollX,
  };
}
export const clickPreventDefaultAndStopPropagation = (e: React.MouseEvent | React.ChangeEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

//* Dates
export const displayMessageDate = (date: string) => {
  const targetDate = new Date(date);
  const currentDate = new Date();
  targetDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  const timeDifferenceInDays = (currentDate.getTime() - targetDate.getTime()) / (24 * 60 * 60 * 1000);
  return timeDifferenceInDays;
};
export function convertSecondstoTime(secondsNumber: number) {
  const dateObj = new Date(secondsNumber * 1000);
  // const hours = dateObj.getUTCHours();
  const minutes = dateObj.getUTCMinutes();
  const seconds = dateObj.getSeconds();

  const timeString = minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');

  return timeString;
}
export const getDateParts = (dateParam: Date | null = null) => {
  const date = dateParam || new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(7, '0');

  return {
    day,
    month,
    year,
    hours,
    minutes,
    seconds,
    milliseconds,
  };
};
export function getCurrentDateFormatted() {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const formattedMonth = month < 10 ? `0${month}` : month;
  const formattedDay = day < 10 ? `0${day}` : day;

  return `${year}-${formattedMonth}-${formattedDay}`;
}

//* Files
export const getFileType = (str: string) => {
  if (!str) return null;
  const splitFileName = str.split('.');
  const fileType = splitFileName[splitFileName.length - 1].toLowerCase();
  return fileType;
};
export function getFileFormatFromURL(url: string) {
  if (!url) return url;
  const fileExtension = url.split('.').pop();
  return fileExtension?.toLowerCase();
}
export function getFileNameFromURL(url: string) {
  if (!url) return url;
  const fileExtension = url.split('/').pop();
  return fileExtension?.toLowerCase();
}
export const getFileTypeByLink = (link: string) => {
  const format = '.' + getFileFormatFromURL(link);
  let mediaType = '';

  if (
    format.toLowerCase() === '.png' ||
    format.toLowerCase() === '.jpg' ||
    format.toLowerCase() === '.jpeg'
  ) {
    mediaType = 'image';
  } else {
    var fileType = '';
    switch (format.toLowerCase()) {
      case '.pdf':
        fileType = 'pdf';
        break;
      case '.xlsx':
        fileType = 'xlsx';
        break;
      case '.mp3':
        fileType = 'audio';
        break;
      case '.m4a':
        fileType = 'audio';
        break;
      case '.z': /// android
        fileType = 'audio';
        //fileLink = model.imageLink.Replace(".z", ".ogg");
        break;
      case '.acc':
        fileType = 'audio';
        break;
      case '.aac':
        fileType = 'audio';
        break;
      case '.3gp':
        fileType = 'audio';
        break;
      case '.amr':
        fileType = 'audio';
        break;
      case '.ogv':
        fileType = 'audio';
        break;
      case '.mp4':
        fileType = 'video';
        break;
      case '.zip':
        fileType = 'zip';
        break;
      case '.mpeg':
        fileType = 'video';
        break;
      case '.mov':
        fileType = 'video';
        break;
      case '.docx':
        fileType = 'docx';
        break;
      case '.gif':
        fileType = 'image';
        break;
      case '.png':
        fileType = 'image';
        break;
      case '.jpg':
        fileType = 'image';
        break;
      case '.jpeg':
        fileType = 'image';
        break;
      default:
        fileType = 'document';
        break;
    }
    mediaType = fileType;
  }

  return mediaType;
};
export function compressImage(file: File | Blob, callback: (result: File | Blob) => void) {
  new Compressor(file, {
    quality: 0.6, // compress level
    maxWidth: 1000,
    maxHeight: 1200,
    success(result) {
      callback(result);
    },
    error(err) {
      console.error('Compressing error: ', err.message);
    },
  });
}

//* Lists & sorting
export function sortListByParam(list: Array<any>, param: string) {
  return [...list].sort((a, b) => a[param] - b[param]);
}
export const isDuplicate = (array: any[], key: string, value: any, index: number) => {
  if (index <= 0 || index >= array.length) {
    return false;
  }

  for (let i = 0; i < index; i++) {
    if (array[i][key] === value) {
      return true;
    }
  }

  return false;
};

//* Notification
export const browserPermissionRequest = () => {
  if (!('Notification' in window)) return;
  //TODO if (applicationType === 'extension') {
  //   sendMessageToBackground('browserNotifPermReq');
  // } else {
  if (Notification.permission === 'default' || Notification.permission === 'denied') {
    Notification.requestPermission();
    //? .then((permissionResult) => {
    // browserNotifPermissionRef.current = permissionResult;
    // console.log("Permission: ", permissionResult);
    // });
  }
  // else browserNotifPermissionRef.current = Notification.permission;
  // }
};
export const handleBrowserNotification = (title: string, content: string, icon?: string | null) => {
  //TODO if (applicationType === 'extension') {
  //   sendMessageToBackground('showBrowserNotif', {
  //     title: title,
  //     content: content,
  //     icon: icon,
  //   });
  // }
  if (!('Notification' in window)) return;
  // if (applicationType !== 'extension') {
  const permission = Notification.permission;
  console.log('handleBrowserNotification perm: ', permission);
  if (permission === 'granted') {
    const notification = new Notification(title, {
      body: content,
      icon: icon || getImageUrl('icon-logo.svg', ''),
    });

    notification.onclick = () => {
      window.focus();
    };
  } else {
    console.log('Browser notifications are denied by user');
  }
  // }
};
export const getBrowserNotifStatus = () => {
  let status: NotificationPermission = 'default';
  if (!('Notification' in window)) status = 'denied';
  else status = Notification.permission;

  return status;
};

//* Messages and templates
export const formSendTemplateObject = async (
  to: string,
  varsData: TemplateVarsData,
  template: WhatsappTemplate | undefined,
  originalTemplate: WhatsappOriginalTemplate,
  fileName?: string,
) => {
  try {
    const formattedParams: Record<TemplateVarsData[0]['name'], string> = varsData.reduce(
      (acc, param) => {
        acc[param.name] = param.text || '';
        return acc;
      },
      {} as Record<TemplateVarsData[0]['name'], string>,
    );

    const params: GetTemplateFormattedObjectParams = {
      templateObject: template,
      templateOriginalObject: originalTemplate,
      to: to,
      templateVars: formattedParams,
    };

    if (Object.keys(formattedParams).some((objKey) => objKey === 'data') && fileName) {
      params.fileName = fileName;
    }

    const object = await getTemplateFormattedObject(params);

    if (object) {
      const { frontTemplateObj, components } = object;

      // Extract components array from frontTemplateObj.template.components
      const componentsArray = frontTemplateObj.template.components.map((comp) => ({
        type: comp.type,
        parameters: comp.parameters || [],
      }));

      let sendParams: FetchSendTemplateMessageParams = {
        id: to,
        templateName: frontTemplateObj.template.name,
        languageCode: frontTemplateObj.template.language.code,
        message: JSON.stringify(components),
        components: componentsArray,
      };

      return { sendParams, frontTemplateObj, components, formattedParams };
    } else {
      return null;
    }
  } catch (error) {
    console.warn('formSendTemplateObject error: ', error);
    return null;
  }
};
